import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import { AuthProvider } from './contexts/AuthContext'
import { FlagsProvider } from './contexts/FlagsProvider'
import { ActiveTeamProvider } from './contexts/ActiveTeamContext'
import App from './App'
import { Toaster } from 'sonner'
import { APP_CONSOLE_NAME, APP_NAME } from './constants/brand'
import { createDebug, createNadDebug } from './utils/debugLog'
import { ConsentProvider } from './consent/ConsentProvider'
import { ConsentAuthBridge } from './consent/ConsentAuthBridge'
import { LocaleSync } from './i18n/useLocaleSync'
import i18n from './i18n/i18n'
import './styles/globals.css'

// The console ships NO third-party monitoring/analytics SDK (Mixpanel removed
// in RQ78378, Sentry removed in RFC-1172). The only non-essential collector is
// Dreamwrite's own first-party product-analytics event log, gated on cookie
// consent (RQ78378/RQ78379) by ConsentProvider → analyticsGate.
const monitoringLog = createDebug('dw:analytics-removal')
const brandLog = createNadDebug('dw:brand-rename')

document.title = APP_CONSOLE_NAME
brandLog({
  event: 'brand-configured',
  trace_id: 'brand-runtime-copy',
  appName: APP_NAME,
  consoleName: APP_CONSOLE_NAME,
})

monitoringLog({
  event: 'monitoring-init',
  sentry: false,
  mixpanel: false,
  mode: import.meta.env.MODE,
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

const rootElement = document.getElementById('root')!
// Nonvisual diagnostic hook for release/version tests. Keep this independent
// from the employee-only build badge so consumer-facing UI stays clean.
rootElement.dataset.appBuildVersion = import.meta.env.VITE_APP_BUILD_VERSION ?? 'unknown'

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* ConsentProvider gates the whole subtree: until the user acknowledges
            the blocking cookie modal, AuthProvider/App do not mount and no
            first-party cookie/storage is written (RQ10409305 / RQ10773473). */}
        <ConsentProvider>
          <I18nextProvider i18n={i18n}>
            <AuthProvider>
              <LocaleSync />
              {/* Bridges auth→consent: writes the account-link audit row on sign-in. */}
              <ConsentAuthBridge />
              {/* RFC-0856 I10: browser flag reader. Inside AuthProvider (so it
                  keys on the user + only fetches once authed) and inside
                  ConsentProvider (so no pre-consent network). */}
              <FlagsProvider>
                {/* RFC-1371 D6: the active-team context. Inside FlagsProvider
                    because its team query is gated on `teams_enabled` — with
                    the flag off it issues no requests and every consumer sees
                    an empty team list. */}
                <ActiveTeamProvider>
                  <App />
                </ActiveTeamProvider>
              </FlagsProvider>
              <Toaster
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: 'var(--color-surface-2)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text-primary)',
                  },
                }}
              />
            </AuthProvider>
          </I18nextProvider>
        </ConsentProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
